import { useState, useEffect } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Buildings, Check, CaretUpDown, SealCheck } from "@phosphor-icons/react";
import { toast } from "sonner";

// Country -> State -> City -> Industry -> Organization -> Department (generic label + value).
// Works unauthenticated (registration) via /public/* + /geo/* endpoints.
export function LocationOrgPicker({ value, onChange, industryTypes = [], departmentLabels = [] }) {
  const v = value || {};
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);

  useEffect(() => {
    api.get("/geo/countries").then((r) => setCountries(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (!v.country) { setStates([]); return; }
    api.get(`/geo/states?country=${encodeURIComponent(v.country)}`).then((r) => setStates(r.data)).catch(() => setStates([]));
  }, [v.country]);

  const set = (patch) => onChange({ ...v, ...patch });
  const labels = departmentLabels.length ? departmentLabels : ["Department", "Division", "Branch", "Team", "Office", "Unit", "Station", "Campus", "Location", "District", "Section", "Facility"];

  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label>Country <span className="text-primary">*</span></Label>
          <Select value={v.country || ""} onValueChange={(c) => set({ country: c, state: "", orgId: "", orgName: "" })}>
            <SelectTrigger data-testid="picker-country" className="mt-1.5 bg-secondary/50"><SelectValue placeholder="Select country" /></SelectTrigger>
            <SelectContent className="max-h-72">{countries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>State / Province / Region{states.length ? <span className="text-primary"> *</span> : ""}</Label>
          {states.length ? (
            <Select value={v.state || ""} onValueChange={(s) => set({ state: s, orgId: "", orgName: "" })} disabled={!v.country}>
              <SelectTrigger data-testid="picker-state" className="mt-1.5 bg-secondary/50"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent className="max-h-72">{states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          ) : (
            <Input data-testid="picker-state-input" className="mt-1.5 bg-secondary/50" value={v.state || ""} disabled={!v.country}
              onChange={(e) => set({ state: e.target.value, orgId: "", orgName: "" })} placeholder="Region (if applicable)" />
          )}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label>City / Municipality</Label>
          <Input data-testid="picker-city" className="mt-1.5 bg-secondary/50" value={v.city || ""} disabled={!v.country}
            onChange={(e) => set({ city: e.target.value, orgId: "", orgName: "" })} placeholder="Optional but recommended" />
        </div>
        <div>
          <Label>Industry <span className="text-primary">*</span></Label>
          <Select value={v.industry || ""} onValueChange={(i) => set({ industry: i, orgId: "", orgName: "" })} disabled={!v.country}>
            <SelectTrigger data-testid="picker-industry" className="mt-1.5 bg-secondary/50"><SelectValue placeholder="Select industry" /></SelectTrigger>
            <SelectContent className="max-h-72">{industryTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label>Organization / Employer <span className="text-primary">*</span></Label>
        <div className="mt-1.5">
          <OrgSearch v={v} onSelect={(o) => set({ orgId: o.id, orgName: o.name, orgType: o.type, deptLabel: v.deptLabel || o.department_label || "Department" })} />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label>Department type <span className="text-muted-foreground text-xs">(optional)</span></Label>
          <Select value={v.deptLabel || "Department"} onValueChange={(d) => set({ deptLabel: d })}>
            <SelectTrigger data-testid="picker-dept-label" className="mt-1.5 bg-secondary/50"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72">{labels.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>{v.deptLabel || "Department"} name <span className="text-muted-foreground text-xs">(optional)</span></Label>
          <Input data-testid="picker-unit" className="mt-1.5 bg-secondary/50" value={v.unit || ""} onChange={(e) => set({ unit: e.target.value })} placeholder={`e.g. ${deptPlaceholder(v.deptLabel)}`} />
        </div>
      </div>
    </div>
  );
}

function deptPlaceholder(label) {
  const map = { Station: "Station 12", Campus: "North Campus", District: "District 4", Unit: "ICU", Division: "32 Division", Team: "Night Team", Office: "Downtown Office", Facility: "Plant 2" };
  return map[label] || "Emergency Department";
}

function OrgSearch({ v, onSelect }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [orgs, setOrgs] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [add, setAdd] = useState({ name: "", website: "", email_domain: "" });
  const [saving, setSaving] = useState(false);
  const disabled = !v.country || !v.industry;

  useEffect(() => {
    if (!open || disabled) return;
    const params = new URLSearchParams();
    if (v.country) params.set("country", v.country);
    if (v.state) params.set("state", v.state);
    if (v.city) params.set("city", v.city);
    if (v.industry) params.set("industry", v.industry);
    if (q) params.set("q", q);
    api.get(`/public/organizations?${params.toString()}`).then((r) => setOrgs(r.data)).catch(() => setOrgs([]));
  }, [open, v.country, v.state, v.city, v.industry, q]);

  const submitAdd = async () => {
    const name = (add.name || q).trim();
    if (!name) { toast.error("Enter your organization's name."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/organizations/request", {
        name, type: v.industry || "Other", country: v.country, state: v.state, city: v.city,
        website: add.website.trim(), email_domain: add.email_domain.trim(),
      });
      const org = data.organization;
      if (data.status === "exists") toast.info("That organization already exists — selected it.");
      else if (data.verified) toast.success("Added & verified instantly ✓");
      else toast.success("Added — pending admin review. You can use it now.");
      onSelect(org);
      setOpen(false); setShowAdd(false); setAdd({ name: "", website: "", email_domain: "" });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setSaving(false);
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setShowAdd(false); }} modal={true}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" data-testid="picker-org-trigger" className="w-full justify-between bg-secondary/50 font-normal" disabled={disabled}>
          <span className="flex items-center gap-2 truncate"><Buildings className="h-4 w-4 text-muted-foreground" weight="bold" />{v.orgName || (disabled ? "Select country & industry first" : "Search your employer…")}</span>
          <CaretUpDown className="h-4 w-4 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <div className="p-2 border-b border-border">
            <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="picker-org-search" className="h-8 bg-secondary/50" placeholder={`Search ${v.industry || "organization"}…`} />
          </div>
          <CommandList>
            <CommandEmpty>
              <div className="py-3 px-2 text-sm text-muted-foreground">No organizations found.</div>
            </CommandEmpty>
            <CommandGroup>
              {orgs.map((o) => (
                <CommandItem key={o.id} value={o.id} data-testid={`picker-org-${o.id}`} onSelect={() => { onSelect(o); setOpen(false); }}>
                  <span className="flex-1 flex items-center gap-1"><span className="font-medium">{o.name}</span>{o.verified && <SealCheck className="h-3.5 w-3.5 text-sky-400" weight="fill" />}<span className="text-muted-foreground text-xs">· {o.city ? o.city + ", " : ""}{o.state || o.country}</span></span>
                  <Check className={`h-4 w-4 ${v.orgId === o.id ? "opacity-100" : "opacity-0"}`} />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
          <div className="border-t border-border p-2">
            {!showAdd ? (
              <button onClick={() => { setShowAdd(true); setAdd((a) => ({ ...a, name: q })); }} data-testid="picker-org-add-toggle" className="text-xs text-primary hover:underline w-full text-left">Can't find your organization? Add it.</button>
            ) : (
              <div className="space-y-2" data-testid="picker-org-add-form">
                <p className="text-xs font-semibold">Add your organization</p>
                <p className="text-[10px] text-muted-foreground">{[v.industry, v.city, v.state, v.country].filter(Boolean).join(" · ")}</p>
                <Input value={add.name} onChange={(e) => setAdd({ ...add, name: e.target.value })} data-testid="picker-org-add-name" className="h-8 bg-secondary/50 text-xs" placeholder="Organization name" />
                <Input value={add.website} onChange={(e) => setAdd({ ...add, website: e.target.value })} data-testid="picker-org-add-website" className="h-8 bg-secondary/50 text-xs" placeholder="Website (optional)" />
                <Input value={add.email_domain} onChange={(e) => setAdd({ ...add, email_domain: e.target.value })} data-testid="picker-org-add-domain" className="h-8 bg-secondary/50 text-xs" placeholder="Work email domain, e.g. nhs.uk (optional)" />
                <Button size="sm" className="w-full rounded-md font-semibold" disabled={saving} onClick={submitAdd} data-testid="picker-org-add-submit">{saving ? "Adding…" : "Add & select"}</Button>
                <p className="text-[10px] text-muted-foreground">A government email or matching work-email domain verifies instantly.</p>
              </div>
            )}
          </div>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
